/**
 * Per-ability "feel" (pure data): how heavy each player ability should land. The VFX table
 * (fx/table.ts) says what an ability looks like; this says how hard it hits the senses:
 *  - `weight` 0..1 drives hit-stop, camera kick, enemy flinch and the impact sound's weight;
 *  - `sound` picks the impact sound id (audio/bank.ts), `swing` an optional whoosh on release;
 *  - `finisher` spenders / executes get an extra beat (bigger stop, kill pop);
 *  - `ranged` casters get a softer camera (no FOV kick, half shake): a healer or caster
 *    standing back should feel their spells land without the screen bouncing.
 * Anything unmapped falls back to the impact style (`styleFeel`).
 */
import { BASE_VISUAL_ALIAS } from '@keystone/content'; // +classes
import { ABILITY, BASE_ABILITY, BLOODREAVER, IRONCLAD, RAVAGER } from '@keystone/content';
import { CINDERBORN, CINDERBORN_AURA, RIMECALLER, STORMCALLER, STORMCALLER_AURA } from '@keystone/content'; // +mage-specs
import { AUTO_SHOT_ABILITY_ID, MELEE_ABILITY_ID } from '@keystone/sim';
import { MIRAGE, SHADOWDANCER, VENOMWRIGHT, VENOMWRIGHT_AURA } from '@keystone/content'; // +rogue-specs
import { HEMOMANCER, HEMOMANCER_AURA, HERETIC, LIGHTBINDER, LIGHTBINDER_AURA } from '@keystone/content'; // +acolyte-specs
import { BEASTBOUND, BEASTBOUND_AURA, DEADEYE, DEADEYE_AURA, GROVEWARDEN, GROVEWARDEN_AURA } from '@keystone/content'; // +ranger-specs
import type { ImpactStyle } from '../fx/table';

export type SwingSound = 'swing' | 'swing.heavy';

export interface FeelSpec {
  weight: number;
  /** Impact sound id (audio/bank.ts), or null for silent. */
  sound: string | null;
  /** Whoosh played when the ability is released (melee swings). */
  swing?: SwingSound;
  /** Release sound for spells (projectile launch, nova). */
  cast?: string;
  /** +audio: charge-up sound when your cast bar starts (cast-time spells; default 'cast.charge'). */
  charge?: string;
  finisher?: boolean;
  ranged?: boolean;
}

const B = ABILITY.blademaster;
const W = ABILITY.warden;
const S = ABILITY.stormcaller;
const L = ABILITY.lightbinder;
const WA = BASE_ABILITY.warrior; // +basekits
const RO = BASE_ABILITY.rogue;
const MA = BASE_ABILITY.mage;
const AC = BASE_ABILITY.acolyte;
const RA = BASE_ABILITY.ranger;

/**
 * +specs: specialisation abilities (docs/design/classes/specs.md). Class agents add their specs here;
 * anything missing falls back through ui.animAs (BASE_VISUAL_ALIAS) to the borrowed ability's feel.
 */
const SPEC_FEEL: Readonly<Record<string, FeelSpec>> = {
  // Ironclad: iron and rivets.
  [IRONCLAD.rivetStrike]: { weight: 0.6, sound: 'spec.plate', swing: 'swing.heavy' },
  [IRONCLAD.anchorChain]: { weight: 0.5, sound: 'hit.blunt' },
  [IRONCLAD.shieldSweep]: { weight: 0.42, sound: 'hit.shield', swing: 'swing' },
  [IRONCLAD.shrapnelBurst]: { weight: 0.85, sound: 'spec.shrapnel', cast: 'nova', finisher: true },
  [IRONCLAD.wreckage]: { weight: 0.7, sound: 'hit.shield', swing: 'swing.heavy' },
  [IRONCLAD.platedBash]: { weight: 0.45, sound: 'hit.shield', swing: 'swing' },
  // Ravager: two greatswords, nonstop.
  [RAVAGER.twinReaping]: { weight: 0.55, sound: 'spec.reap', swing: 'swing.heavy', finisher: true },
  [RAVAGER.breakneck]: { weight: 0.6, sound: 'hit.blunt' },
  [RAVAGER.steelCyclone]: { weight: 0.38, sound: 'hit.blade', swing: 'swing.heavy' },
  [RAVAGER.redHunger]: { weight: 0.48, sound: 'hit.blade.heavy', swing: 'swing' },
  [RAVAGER.headsman]: { weight: 1, sound: 'hit.execute', swing: 'swing.heavy', finisher: true },
  [RAVAGER.hiltCrack]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  // Bloodreaver: a red greatsword that drinks.
  [BLOODREAVER.bloodwrench]: { weight: 0.9, sound: 'spec.drink', swing: 'swing.heavy', finisher: true },
  [BLOODREAVER.gorecharge]: { weight: 0.6, sound: 'hit.blunt' },
  [BLOODREAVER.rendingArc]: { weight: 0.45, sound: 'hit.blade.heavy', swing: 'swing.heavy' },
  [BLOODREAVER.bleedingEdge]: { weight: 0.5, sound: 'hit.blade.heavy', swing: 'swing' },
  [BLOODREAVER.finalDraught]: { weight: 1, sound: 'spec.drink', swing: 'swing.heavy', finisher: true },
  [BLOODREAVER.crimsonKick]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  [BLOODREAVER.bloodletting]: { weight: 0.2, sound: 'spec.bleed' },
  // +mage-specs. Stormcaller: crackle, then Skybreak's thunder.
  [STORMCALLER.spark]: { weight: 0.34, sound: 'hit.lightning', charge: 'cast.charge.storm', ranged: true },
  [STORMCALLER.skybreak]: { weight: 0.95, sound: 'spec.skybreak', finisher: true, ranged: true },
  [STORMCALLER.staticCage]: { weight: 0.35, sound: 'spec.static', cast: 'nova', ranged: true },
  [STORMCALLER.staticStep]: { weight: 0.2, sound: 'spec.static', ranged: true },
  [STORMCALLER.chainStorm]: { weight: 0.45, sound: 'chain', ranged: true },
  [STORMCALLER.arcWard]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  [STORMCALLER.wanderingTempest]: { weight: 0.25, sound: 'hit.lightning', cast: 'star.launch', ranged: true },
  [STORMCALLER.stormheart]: { weight: 0, sound: null, cast: 'nova', ranged: true },
  // Rimecaller: frost; a fractured Hoarlance lands hard.
  [RIMECALLER.rimeTithe]: { weight: 0.34, sound: 'hit.frost', charge: 'cast.charge', ranged: true },
  [RIMECALLER.hoarlance]: { weight: 0.85, sound: 'spec.shatter', finisher: true, ranged: true },
  [RIMECALLER.rimeRing]: { weight: 0.4, sound: 'spec.freeze', cast: 'nova', ranged: true },
  [RIMECALLER.frostfold]: { weight: 0.3, sound: 'spec.freeze', ranged: true },
  [RIMECALLER.whiteout]: { weight: 0.15, sound: 'hit.frost', ranged: true },
  [RIMECALLER.frostMantle]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  [RIMECALLER.wanderingGlacier]: { weight: 0.3, sound: 'spec.freeze', cast: 'star.launch', ranged: true },
  [RIMECALLER.deepWinter]: { weight: 0.6, sound: 'spec.freeze', cast: 'nova', ranged: true },
  // Cinderborn: fire paid for in blood; Cinderfall is the crater.
  [CINDERBORN.emberLash]: { weight: 0.38, sound: 'hit.fire', cast: 'cast.fire', charge: 'cast.charge.fire', ranged: true },
  [CINDERBORN.cinderfall]: { weight: 1, sound: 'spec.meteor', cast: 'cast.fire', finisher: true, ranged: true },
  [CINDERBORN.infernoRing]: { weight: 0.4, sound: 'hit.fire', cast: 'nova', ranged: true },
  [CINDERBORN.phoenixDive]: { weight: 0.45, sound: 'hit.fire', ranged: true },
  [CINDERBORN.pyreBurst]: { weight: 0.55, sound: 'hit.fire', ranged: true },
  [CINDERBORN.ashrisePact]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  [CINDERBORN.wanderingSun]: { weight: 0.25, sound: 'hit.fire.small', cast: 'star.launch', ranged: true },
  [CINDERBORN.kindledHeart]: { weight: 0, sound: null, cast: 'spec.rise', ranged: true },
  // Passive moments: the Overvolt burn crackles; Ashrise is the phoenix.
  [STORMCALLER_AURA.stormcell]: { weight: 0.05, sound: 'spec.static', ranged: true },
  [CINDERBORN_AURA.ashrise]: { weight: 0.8, sound: 'spec.rise', finisher: true, ranged: true },
  // +rogue-specs: Shadowdancer (shadow cuts, the clones' echoes), Mirage (mirror-glass), Venomwright (toxins).
  [SHADOWDANCER.twinFang]: { weight: 0.32, sound: 'hit.blade', swing: 'swing' },
  [SHADOWDANCER.umbralFinale]: { weight: 0.8, sound: 'spec.umbral', swing: 'swing.heavy', finisher: true },
  [SHADOWDANCER.gloamstrike]: { weight: 0.6, sound: 'spec.umbral', swing: 'swing' },
  [SHADOWDANCER.shadowFan]: { weight: 0.38, sound: 'hit.blade', swing: 'swing' },
  [SHADOWDANCER.shadeJab]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  [SHADOWDANCER.silhouetteEcho]: { weight: 0.45, sound: 'spec.echo' },
  [SHADOWDANCER.silhouetteFan]: { weight: 0.15, sound: 'spec.echo' },
  [MIRAGE.falsestep]: { weight: 0.4, sound: 'hit.blade', swing: 'swing' },
  [MIRAGE.returnBlow]: { weight: 0.75, sound: 'spec.mirror', swing: 'swing.heavy', finisher: true },
  [MIRAGE.mirrorSpin]: { weight: 0.4, sound: 'hit.blade', swing: 'swing' },
  [MIRAGE.mirrorJab]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  [MIRAGE.phantomLure]: { weight: 0.2, sound: 'spec.mirror' },
  [VENOMWRIGHT.venomEdge]: { weight: 0.35, sound: 'hit.venom', swing: 'swing' },
  [VENOMWRIGHT.catalyst]: { weight: 0.85, sound: 'spec.catalyst', swing: 'swing.heavy', finisher: true },
  [VENOMWRIGHT.paralyticOpener]: { weight: 0.6, sound: 'hit.venom', swing: 'swing' },
  [VENOMWRIGHT.flaskToss]: { weight: 0.35, sound: 'hit.venom' },
  [VENOMWRIGHT.numbingJab]: { weight: 0.3, sound: 'hit.venom', swing: 'swing' },
  [VENOMWRIGHT_AURA.catalysis]: { weight: 0.55, sound: 'spec.catalyst', finisher: true },
  // +acolyte-specs. Lightbinder: bright holy hits; Absolution is a soft chime on the healed.
  [LIGHTBINDER.dawnlash]: { weight: 0.4, sound: 'hit.holy', cast: 'cast.holy', ranged: true },
  [LIGHTBINDER.triuneRay]: { weight: 0.32, sound: 'spec.ray', ranged: true },
  [LIGHTBINDER.quickmend]: { weight: 0, sound: 'heal.big', ranged: true },
  [LIGHTBINDER.radiantWard]: { weight: 0, sound: null, cast: 'spec.ward', ranged: true },
  [LIGHTBINDER.haloburst]: { weight: 0.2, sound: 'heal', cast: 'nova', ranged: true },
  [LIGHTBINDER.blindingVerdict]: { weight: 0.6, sound: 'hit.holy.big', cast: 'nova', ranged: true },
  [LIGHTBINDER.exaltation]: { weight: 0, sound: null, cast: 'spec.ward', ranged: true },
  [LIGHTBINDER_AURA.absolution]: { weight: 0, sound: 'spec.absolve', ranged: true },
  // Hemomancer: wet, heavy blood; Martyr's payback is the big swell.
  [HEMOMANCER.bloodBolt]: { weight: 0.38, sound: 'spec.blood', charge: 'cast.charge', ranged: true },
  [HEMOMANCER.transfusion]: { weight: 0, sound: 'heal.big', ranged: true },
  [HEMOMANCER.crimsonBloom]: { weight: 0, sound: 'heal', cast: 'nova', ranged: true },
  [HEMOMANCER.bleedfield]: { weight: 0.2, sound: 'spec.blood', ranged: true },
  [HEMOMANCER.coagulate]: { weight: 0.45, sound: 'spec.blood', ranged: true },
  [HEMOMANCER.martyr]: { weight: 0, sound: null, cast: 'spec.martyr', ranged: true },
  [HEMOMANCER_AURA.martyr]: { weight: 0.1, sound: 'spec.bleed', ranged: true },
  // Heretic: hollow shadow; Soulflay and the Hollow One's arrival land hard.
  [HERETIC.hexbolt]: { weight: 0.36, sound: 'hit.shadow', charge: 'cast.charge', ranged: true },
  [HERETIC.rotcurse]: { weight: 0.2, sound: 'spec.curse', ranged: true },
  [HERETIC.unholyTithe]: { weight: 0.15, sound: 'spec.bleed', ranged: true },
  [HERETIC.wraithWard]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  [HERETIC.blightField]: { weight: 0.2, sound: 'spec.curse', ranged: true },
  [HERETIC.dreadGaze]: { weight: 0.3, sound: 'spec.curse', ranged: true },
  [HERETIC.soulflay]: { weight: 0.85, sound: 'spec.soulflay', finisher: true, ranged: true },
  [HERETIC.callHollowOne]: { weight: 0.9, sound: 'spec.hollow', cast: 'nova', finisher: true, ranged: true },
  [HERETIC.hollowRend]: { weight: 0.35, sound: 'hit.blade.heavy' },
  // +ranger-specs. Deadeye: a gunshot crack on every round; the perfect reload's click and chime.
  [DEADEYE.quickdraw]: { weight: 0.4, sound: 'hit.arrow', cast: 'spec.gunshot', ranged: true },
  [DEADEYE.fanTheHammer]: { weight: 0.85, sound: 'hit.arrow', cast: 'spec.gunshot.fan', finisher: true, ranged: true },
  [DEADEYE.ricochet]: { weight: 0.5, sound: 'spec.ricochet', cast: 'spec.gunshot', ranged: true },
  [DEADEYE.disarmingShot]: { weight: 0.35, sound: 'hit.arrow', cast: 'spec.gunshot', ranged: true },
  [DEADEYE.flashbang]: { weight: 0.55, sound: 'hit.holy.big', ranged: true },
  [DEADEYE.rollReload]: { weight: 0, sound: null, cast: 'spec.reload', ranged: true },
  [DEADEYE.snapShut]: { weight: 0, sound: null, cast: 'spec.reload', ranged: true },
  [DEADEYE.highNoon]: { weight: 0, sound: null, cast: 'spec.highnoon', ranged: true },
  [DEADEYE_AURA.highNoonMark]: { weight: 0.9, sound: 'spec.gunshot.fan', finisher: true, ranged: true },
  // Grovewarden: soft blooms; Thorn Whip is a lash, Snarlroot cracks the ground.
  [GROVEWARDEN.budding]: { weight: 0.3, sound: 'spec.thorn', ranged: true },
  [GROVEWARDEN.burstbloom]: { weight: 0, sound: 'spec.bloom', ranged: true },
  [GROVEWARDEN.lifevine]: { weight: 0, sound: 'heal', ranged: true },
  [GROVEWARDEN.overgrowthSurge]: { weight: 0, sound: 'heal', cast: 'spec.bloom', ranged: true },
  [GROVEWARDEN.snarlroot]: { weight: 0.35, sound: 'spec.roots', cast: 'nova', ranged: true },
  [GROVEWARDEN.worldroot]: { weight: 0, sound: null, cast: 'spec.roots', ranged: true },
  [GROVEWARDEN.thornpin]: { weight: 0.3, sound: 'spec.thorn', ranged: true },
  [GROVEWARDEN_AURA.lifevine]: { weight: 0, sound: 'spec.bud', ranged: true },
  [GROVEWARDEN_AURA.budding]: { weight: 0, sound: 'spec.bud', ranged: true },
  // Beastbound: arrows and the spirit beast's bites; the howl and the herd.
  [BEASTBOUND.markPrey]: { weight: 0.38, sound: 'hit.arrow', cast: 'bow.shot', ranged: true },
  [BEASTBOUND.packFury]: { weight: 0.75, sound: 'hit.arrow', cast: 'bow.shot', finisher: true, ranged: true },
  [BEASTBOUND.spiritHerd]: { weight: 0.6, sound: 'spec.herd', ranged: true },
  [BEASTBOUND.howlShot]: { weight: 0.3, sound: 'hit.arrow', ranged: true },
  [BEASTBOUND.spiritMerge]: { weight: 0, sound: null, cast: 'spec.howl', ranged: true },
  [BEASTBOUND.pounce]: { weight: 0.35, sound: 'spec.bite' },
  [BEASTBOUND.furyBite]: { weight: 0.5, sound: 'spec.bite' },
  [BEASTBOUND.spiritPin]: { weight: 0.5, sound: 'spec.maul' },
  [BEASTBOUND.maulBite]: { weight: 0.8, sound: 'spec.maul', finisher: true },
  [BEASTBOUND.spiritRend]: { weight: 0.4, sound: 'spec.bite' },
  [BEASTBOUND.howl]: { weight: 0, sound: null, cast: 'spec.howl' },
  [BEASTBOUND.growl]: { weight: 0.2, sound: 'spec.howl' },
  [BEASTBOUND_AURA.maulBleed]: { weight: 0.05, sound: 'spec.bleed' },
};

export const FEEL: Readonly<Record<string, FeelSpec>> = {
  ...SPEC_FEEL,
  // Blademaster: steel and weight. Execute / Ravager's Finale are the payoffs.
  [MELEE_ABILITY_ID]: { weight: 0.18, sound: 'hit.blade' },
  [B.mortalStrike]: { weight: 0.72, sound: 'hit.blade.heavy', swing: 'swing.heavy' },
  [B.overpower]: { weight: 0.42, sound: 'hit.blade', swing: 'swing' },
  [B.cleave]: { weight: 0.36, sound: 'hit.blade', swing: 'swing' },
  [B.execute]: { weight: 1, sound: 'hit.execute', swing: 'swing.heavy', finisher: true },
  [B.bladestorm]: { weight: 0.12, sound: 'hit.blade' },
  [B.colossusSmash]: { weight: 0.82, sound: 'hit.earth', swing: 'swing.heavy' },
  [B.pummel]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  [B.charge]: { weight: 0.6, sound: 'hit.blunt' },
  [B.slam]: { weight: 0.6, sound: 'hit.blade.heavy', swing: 'swing.heavy' },
  [B.rend]: { weight: 0.4, sound: 'hit.blade', swing: 'swing' },
  [B.ravagersFinale]: { weight: 1, sound: 'hit.execute', swing: 'swing.heavy', finisher: true },
  // Warden: the shield. Clangs and thumps; Shield Slam is the signature.
  [W.shieldSlam]: { weight: 0.75, sound: 'hit.shield', swing: 'swing.heavy' },
  [W.cleaveStrike]: { weight: 0.36, sound: 'hit.blade', swing: 'swing' },
  [W.bulwarkStrike]: { weight: 0.55, sound: 'hit.blunt', swing: 'swing.heavy' },
  [W.consecratedGround]: { weight: 0.3, sound: 'hit.holy', cast: 'nova' },
  [W.shieldBash]: { weight: 0.42, sound: 'hit.shield', swing: 'swing' },
  [W.shieldWallBash]: { weight: 0, sound: null, cast: 'shield.up' },
  [W.lastStand]: { weight: 0, sound: null, cast: 'shield.up' },
  [W.aegisThrow]: { weight: 0.5, sound: 'hit.shield', cast: 'swing' },
  [W.aegisRecall]: { weight: 0.35, sound: 'hit.shield' },
  [W.riposte]: { weight: 0.46, sound: 'hit.blade', swing: 'swing' },
  [W.grip]: { weight: 0.3, sound: 'hit.shadow' },
  // Stormcaller: crackle, fire, and Tempest's thunder.
  [S.lightningBolt]: { weight: 0.36, sound: 'hit.lightning', charge: 'cast.charge.storm', ranged: true },
  [S.lavaBurst]: { weight: 0.66, sound: 'hit.fire', cast: 'cast.fire', charge: 'cast.charge.fire', ranged: true },
  [S.chainLightning]: { weight: 0.3, sound: 'chain', charge: 'cast.charge.storm', ranged: true },
  [S.earthquake]: { weight: 0.1, sound: null, cast: 'nova', ranged: true },
  [S.stormkeeper]: { weight: 0, sound: null, cast: 'nova', ranged: true },
  [S.flameShock]: { weight: 0.22, sound: 'hit.fire.small', ranged: true },
  [S.earthShock]: { weight: 0.62, sound: 'hit.earth', ranged: true },
  [S.arcTorrent]: { weight: 0.2, sound: 'hit.lightning', charge: 'cast.charge.storm', ranged: true },
  [S.tempest]: { weight: 0.92, sound: 'hit.lightning.big', charge: 'cast.charge.storm', finisher: true, ranged: true },
  [S.astralShift]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  // Lightbinder: soft bells; heals bloom. Damage spells still land, gently.
  [L.holyShock]: { weight: 0.4, sound: 'hit.holy', ranged: true },
  [L.judgment]: { weight: 0.42, sound: 'hit.holy', cast: 'cast.holy', ranged: true },
  [L.sunfall]: { weight: 0.8, sound: 'hit.holy.big', cast: 'cast.holy', finisher: true, ranged: true },
  [L.dawnbringer]: { weight: 0.5, sound: 'hit.holy.big', cast: 'nova', ranged: true },
  [L.dawnsong]: { weight: 0.2, sound: 'hit.holy', cast: 'nova', ranged: true },
  [L.radiantWave]: { weight: 0, sound: null, cast: 'nova', ranged: true },
  [L.divineShield]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  [L.guardiansAegis]: { weight: 0, sound: null, cast: 'shield.up', ranged: true },
  // +basekits: the new base-kit abilities (sounds from audio/bank.ts).
  [WA.headlong]: { weight: 0.6, sound: 'hit.blunt', swing: 'swing.heavy' },
  [WA.rive]: { weight: 0.4, sound: 'hit.blade', swing: 'swing' },
  [WA.bloodprize]: { weight: 0.75, sound: 'hit.blade.heavy', swing: 'swing.heavy', finisher: true },
  [RO.lurk]: { weight: 0, sound: null, cast: 'stealth' },
  [RO.dirtyOpener]: { weight: 0.7, sound: 'hit.blunt', swing: 'swing.heavy' },
  [RO.throatjab]: { weight: 0.3, sound: 'hit.blunt', swing: 'swing' },
  [MA.hoarbind]: { weight: 0.35, sound: 'hit.frost', cast: 'nova', ranged: true },
  [MA.wanderingStar]: { weight: 0.25, sound: 'hit.lightning', cast: 'star.launch', ranged: true },
  [AC.dawnbrand]: { weight: 0.3, sound: 'hit.holy', cast: 'cast.holy', ranged: true },
  [AC.hearthward]: { weight: 0, sound: null, cast: 'nova', ranged: true },
  [AC.dazzle]: { weight: 0.5, sound: 'hit.holy.big', ranged: true },
  [RA.snareCoil]: { weight: 0.3, sound: 'trap.spring', ranged: true },
  [RA.swiftfoot]: { weight: 0, sound: null, cast: 'dodge', ranged: true },
  [AUTO_SHOT_ABILITY_ID]: { weight: 0.15, sound: 'auto.shotHit', ranged: true }, // +autoattack (+audio: arrow hit)
  [RA.pinShot]: { weight: 0.3, sound: 'hit.arrow', ranged: true }, // +audio
};

/** Sound and weight by impact style (enemy abilities and anything unmapped). */
const STYLE_FEEL: Readonly<Record<ImpactStyle, FeelSpec>> = {
  none: { weight: 0, sound: null },
  sparks: { weight: 0.2, sound: 'hit.blade' },
  slash: { weight: 0.35, sound: 'hit.blade' },
  heavy: { weight: 0.6, sound: 'hit.blade.heavy' },
  shieldSlam: { weight: 0.65, sound: 'hit.shield' },
  execute: { weight: 0.9, sound: 'hit.execute' },
  smash: { weight: 0.75, sound: 'hit.earth' },
  taunt: { weight: 0, sound: null },
  lightning: { weight: 0.35, sound: 'hit.lightning' },
  fire: { weight: 0.55, sound: 'hit.fire' },
  flameLick: { weight: 0.2, sound: 'hit.fire.small' },
  holy: { weight: 0.35, sound: 'hit.holy' },
  holyBurst: { weight: 0.5, sound: 'hit.holy.big' },
  venom: { weight: 0.3, sound: 'hit.venom' },
  shadow: { weight: 0.35, sound: 'hit.shadow' },
  arcane: { weight: 0.35, sound: 'hit.arcane' },
  frost: { weight: 0.35, sound: 'hit.frost' },
  heal: { weight: 0, sound: 'heal' },
  bigHeal: { weight: 0, sound: 'heal.big' },
  motes: { weight: 0, sound: 'heal' },
  cleanse: { weight: 0, sound: 'heal' },
};

export function styleFeel(style: ImpactStyle): FeelSpec {
  return STYLE_FEEL[style] ?? STYLE_FEEL.none;
}

/** Feel for an ability: explicit table, else by its impact style. */
export function feelFor(abilityId: string, style: ImpactStyle): FeelSpec {
  return FEEL[abilityId] ?? FEEL[BASE_VISUAL_ALIAS[abilityId] ?? ''] ?? styleFeel(style); // +classes
}

/** Heal sound for a heal-style impact (`heal` / `bigHeal` / `holyBurst` / `motes`). */
export function healSound(style: ImpactStyle, crit: boolean): string | null {
  if (style === 'none') return null;
  return style === 'bigHeal' || style === 'holyBurst' || crit ? 'heal.big' : 'heal';
}
